import { Box, clsx, H1, H2, H3, H4, P, Spinner } from '@a-type/ui';
import { LongGameError } from '@long-game/common';
import { fetch, queryClient, useSuspenseQuery } from '@long-game/game-client';
import { useGame, withSuspense } from '@long-game/game-ui';
import { Link } from '@tanstack/react-router';
import { lazy, useLayoutEffect, useRef } from 'react';
import type { Components } from 'react-markdown';
import customHeaderId from 'remark-custom-header-id';
import mdc from 'remark-mdc';
import cls from './GameManual.module.css';

const LazyMarkdown = lazy(() => import('react-markdown'));

export interface GameManualProps {
  gameId: string;
}

const plugins = [mdc, customHeaderId];

export const GameManual = withSuspense(
  function GameManual({ gameId }: GameManualProps) {
    const game = useGame(gameId);
    const latestVersion = game?.versions.at(-1)?.version ?? 'v1';
    const { data: markdown } = useSuspenseQuery(
      {
        queryKey: ['gameManual', gameId],
        queryFn: async () => {
          const result = await fetch(
            `/game-data/${gameId}/${latestVersion}/rules.md`,
          );
          if (!result.ok) {
            throw LongGameError.fromResponse(result);
          }
          return result.text();
        },
      },
      queryClient,
    );

    const wrapperRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
      const targetId = window.location.hash?.substring(1);
      if (!targetId) return;
      const targetElement = wrapperRef.current?.querySelector(
        `[id="${targetId}"]`,
      );
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        targetElement.classList.add('bg-accent-wash');
      }
    }, []);

    return (
      <Box col gap="lg" ref={wrapperRef}>
        <LazyMarkdown
          urlTransform={(url) => {
            const asUrl = new URL(url, window.location.origin);
            asUrl.pathname = `/game-data/${gameId}/${latestVersion}${asUrl.pathname}`;
            return asUrl.toString();
          }}
          components={markdownComponents}
          remarkPlugins={plugins}
        >
          {markdown}
        </LazyMarkdown>
      </Box>
    );
  },
  <Spinner />,
);
export default GameManual;

const markdownComponents: Components = {
  a: ({ node, ...props }) => {
    return <Link {...props} to={props.href ?? '#'} />;
  },
  img: ({ node, className, ...props }) => {
    return (
      <Box full="width" layout="center center" render={<span />}>
        <img {...props} className={clsx(cls.image, className)} />
      </Box>
    );
  },
  h1: H1 as any,
  h2: H2 as any,
  h3: H3 as any,
  h4: H4 as any,
  p: P as any,
  blockquote: ({ node, className, ...props }) => {
    return (
      <Box
        full="width"
        surface
        p="sm"
        className={clsx(cls.blockquote, className)}
        render={<blockquote {...props} />}
      />
    );
  },
};
